import { DateTime } from 'luxon';
import React, { HTMLProps, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ForecastContext } from '../../../contexts/investment-robot/ForecastContext';
import { LoadingStateWrapper } from '../../general/LoadingStateWrapper';
import { ForecastChart, ForecastChartProperties } from './ForecastChart';
import { ForecastChartMobile } from './ForecastChartMobile';
import { ChartData, ForecastLineChart } from './ForecastLineChart';

export type ForecastChartWrapperPropeties = {
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    isIdle: boolean;
};

export const ForecastChartWrapper: React.FC<
    ForecastChartWrapperPropeties & ForecastChartProperties & HTMLProps<HTMLDivElement>
> = ({ isLoading, isFetching, isError, isIdle, values, goal, type, inYears, deposit, className, ...rest }) => {
    const displayContext = useContext(ForecastContext);
    const { t } = useTranslation('investment');

    const lineChartData = useMemo(() => {
        let pessimisticValues: ChartData = { id: 'pessimistic', label: t("forecast-screen-result-pessimistic-market"), data: [] };
        let medianValues: ChartData = { id: 'median', label: t("forecast-screen-result-expected-market"), data: [] };
        let optimisticValues: ChartData = { id: 'optimistic', label: t("forecast-screen-result-optimistic-market"), data: [] };

        values.forEach((value, i) => {
            const t = DateTime.now().plus({ months: i }).toMillis();
            pessimisticValues.data[i] = { y: value.pessimistic, t: t };
            medianValues.data[i] = { y: value.median, t: t };
            optimisticValues.data[i] = { y: value.optimistic, t: t };
        });

        return {
            pessimistic: pessimisticValues,
            median: medianValues,
            optimistic: optimisticValues,
        };
    }, [values, t]);

    return (
        <LoadingStateWrapper
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            isIdle={isIdle}
            className={className}
            {...rest}
        >
            {displayContext.forecast.parameters.display === 'bar' ? (
                <>
                    <div className="block md:hidden h-full">
                        <ForecastChartMobile
                            deposit={deposit}
                            goal={goal}
                            type={type}
                            inYears={inYears}
                            values={values}
                        />
                    </div>
                    <div className="hidden md:block h-full">
                        <ForecastChart deposit={deposit} goal={goal} type={type} inYears={inYears} values={values} />
                    </div>
                </>
            ) : (
                <ForecastLineChart
                    chartData={[lineChartData.optimistic, lineChartData.median, lineChartData.pessimistic]}
                />
            )}
        </LoadingStateWrapper>
    );
};
